// Type definition for the time range returned
import { TimeService } from "@me8eon/time";
import { timefilterPluginName, TimeFilters, TimeString, timeStrings } from "@me8eon/time_filters";
import { ElasticSearchContext, ElasticSearchFilters } from "./elastic.search.data.source";
import { SearchTextFilter } from "@me8eon/searchtext_filters";
import { restFilterType } from "./elastic.search.filters";
import { companyfilterPluginName, CompanyFilters, CompanyString } from "@me8eon/company_filters";
import { UserData } from "@me8eon/authentication";

type TimeRange = {
    gte: string;
    lt: string;
};

// Helper function to calculate time ranges immutably
export function getTimeRange(timeService: TimeService, timeString: TimeString): TimeRange {
    const now = new Date(timeService());
    const todayString = now.toISOString().split("T")[0];  // Calculate once for reuse

    const calculateRange = (daysToSubtract: number): TimeRange => {
        const calculatedDate = timeService()  // Clone to avoid mutation
        calculatedDate.setDate(calculatedDate.getDate() - daysToSubtract);
        return {
            gte: calculatedDate.toISOString().split("T")[0],  // Start of the calculated day
            lt: todayString,                                   // Start of today
        };
    };

    switch (timeString) {
    case "yesterday":
        return calculateRange(1);
    case "lastWeek":
        return calculateRange(7);
    case "lastMonth": {
        const firstOfThisMonth = new Date(now);
        firstOfThisMonth.setDate(1);  // Go to the first of the current month
        firstOfThisMonth.setMonth(firstOfThisMonth.getMonth() - 1);  // Subtract one month

        const startOfLastMonth = firstOfThisMonth.toISOString().split("T")[0];  // Start of last month
        return {
            gte: startOfLastMonth,
            lt: todayString,  // Up to today (current date)
        };
    }
    case "lastYear": {
        const firstOfThisYear = new Date(now);
        firstOfThisYear.setMonth(0);  // Go to January
        firstOfThisYear.setDate(1);   // Set to the first day of the year
        firstOfThisYear.setFullYear(firstOfThisYear.getFullYear() - 1);  // Subtract one year

        const startOfLastYear = firstOfThisYear.toISOString().split("T")[0];  // Start of last year
        return {
            gte: startOfLastYear,
            lt: todayString,
        };
    }
    default:
        throw new Error(`Unsupported time string: ${timeString}`);
    }
}

export type ElasticsearchRangeQuery = {
    range: {
        [key: string]: TimeRange & { format: string };
    };
};

export function findTimeFilter(
    context: ElasticSearchContext,
    filter: TimeFilters,
    timestampField: string = "last_updated",
): ElasticsearchRangeQuery | undefined {
    const timeString = filter[timefilterPluginName];
    if (timeString) {
        const range = getTimeRange(context.timeService, timeString);
        return {
            range: {
                [timestampField]: {
                    ...range,
                    format: "strict_date_optional_time",
                },
            },
        };

    }
    return undefined;
}

export function findRestFilters(
    filter: SearchTextFilter,
): restFilterType {
    // const excludeKeys: string[] = ["agg:status"];
    const excludeKeys: string[] = [];
    const filterArray = Object.entries(filter)
        .filter(([key, value]) =>
            key.startsWith("agg:") &&
            value &&
            !excludeKeys.includes(key)
        )
        .map(([key, value]) => {
            const field = key.slice(4); // remove "agg:"
            let queryPart;

            switch (key) {
                case "agg:card_languages":
                    queryPart = {
                        terms: {
                            [`${field}.keyword`]: value.split(",")
                        }
                    };
                    break;

                case "agg:companyTags":
                    queryPart = {
                        bool: {
                            should: [
                                { terms: { [`${field}.keyword`]: value.split(",") } },
                                { term: { isPublic: true } }
                            ]
                        }
                    };
                    break;

                default:
                    queryPart = {
                        term: {
                            [`${field}.keyword`]: value
                        }
                    };
                    break;
            }

            return queryPart;
        });
    return filterArray.length > 0 ? filterArray : undefined;
}


type TermQuery = {
  term: Record<string, string | boolean>;
};

type BoolQuery = {
  bool: {
    should: TermQuery[];
    minimum_should_match: number;
  };
};

type ESQuery = BoolQuery;

export function findCompanyFilter(
    context: ElasticSearchContext,
    filter: ElasticSearchFilters,
    companyStringField: string = "companyIdTags",
    userData: UserData
): ESQuery | undefined {
    const companyString = filter[companyfilterPluginName];
    if (filter.dataview === "compass" && companyString !== "compassWide") {
        return {
            bool: {
                should: [
                    { "term": { [`${companyStringField}.keyword`]: userData.companyId } },
                    { "term": { "isPublic": true } }
                ],
                "minimum_should_match": 1
            }
        };

    }
    return undefined;
}